"use client"

import { useState } from "react"
import { Eye, EyeOff, User, Building2, Loader2 } from "lucide-react"
import { post } from "../utility/fetch"
import { toast } from "react-hot-toast"

function LoginForm({ onLogin }) {
  const [loginType, setLoginType] = useState("citizen")
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    const endpoint = loginType === "citizen" ? "/api/auth/citizen-login" : "/api/auth/agency_login"

    try {
      const response = await post(endpoint, {
        username: formData.username,
        password: formData.password,
      })

      if (!response.isSuccess || !response.data) {
        throw new Error(response.error || "Login failed. Please try again.")
      }

      const { token, userId, username, expirationDate } = response.data

      localStorage.setItem("authToken", token)
      localStorage.setItem("userId", userId)
      localStorage.setItem("username", username)
      localStorage.setItem("expirationDate", expirationDate)
      localStorage.setItem("loginType", loginType)

      toast.success(`Logged in as ${loginType}`)

      onLogin?.(loginType)
    } catch (error) {
      console.error("Login error:", error.response)
      toast.error(error.message || "An unexpected error occurred")
    }
    setIsSubmitting(false)
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Feedback Portal</h1>
            <p className="text-gray-600 mt-2">Sign in to your account</p>
          </div>

          {/* Login Type Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              type="button"
              onClick={() => setLoginType("citizen")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${loginType === "citizen" ? "bg-white text-primary shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <User className="w-4 h-4" />
              Citizen
            </button>
            <button
              type="button"
              onClick={() => setLoginType("agency")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${loginType === "agency" ? "bg-white text-primary shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <Building2 className="w-4 h-4" />
              Agency
            </button>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center text-sm text-gray-700">
                <input type="checkbox" className="h-4 w-4 text-primary border-gray-300 rounded mr-2" />
                Remember me
              </label>
              <button
                type="button"
                className="text-sm text-primary hover:text-primary/80 font-medium"
                onClick={() => (window.location.href = "/forgot-password")}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors font-medium justify-center flex items-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <> Sign In as {loginType === "citizen" ? "Citizen" : "Agency User"}</>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{" "}
              <button
                className="text-primary hover:text-primary/80 font-medium"
                onClick={() => (window.location.href = "/sign-up")}
              >
                Sign Up
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginForm
